<template>
	<div class="game" :class="{ shaking }">
		<div class="game-content" ref="game_content" :data-room="current_room" :style="{ width: game_width + 'px' }">
			<background v-if="level.biome" :key="level.biome.slug" :biome="level.biome" :width="bg_width" />
			<board ref="board" :style="{ left: board_left + 'px' }" />
		</div>
		
		<hand />

		<div v-show="game.status === 'ready'" class="start-turn-button" v-hammer:tap="start_turn">
			<div class="shadow" v-bg="'end-turn-button.png'"></div>
			<div class="button" v-bg="'end-turn-button.png'"></div>
			<div class="text">Go!</div>
		</div>

		<transition name="shop">
			<shop v-if="game.shop" />
		</transition>

		<div class="hero-infos">
			<ui-gold :value="game.hero.gold" :reverse="true" />
		</div>

		<div v-if="talismans.length" class="talismans">
			<div v-for="slot in talismans" :key="slot.slug" class="talisman-wrap">
				<talisman :talisman="slot.talisman" />
				<div v-if="slot.count > 1" class="count">{{ slot.count }}</div>
				<ui-tooltip v-if="slot.count <= 1" :title="slot.talisman[lang].name" :text="slot.talisman[lang].description" orientation="bottom" />
				<ui-tooltip v-else :title="slot.talisman[lang].name + ' (x' + slot.count + ')'" :text="slot.talisman[lang].description" orientation="bottom" />
			</div>
		</div>

		<div v-show="game.status !== 'ready' && !game.shop" class="overlay"></div>
		<transition name="opacity">
			<div v-if="game.status === 'loading'" class="overlay dark"></div>
		</transition>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Background from '@/components/game/background'
import Board from '@/components/game/board'
import Hand from '@/components/game/hand'
import Shop from '@/components/game/shop'
import Talisman from '@/components/global/talisman'
import UiGold from '@/components/ui/ui-gold'
import UiTooltip from '@/components/ui/ui-tooltip'
import Sounds from '@/js/sounds'
import { fire_trigger } from '@/js/triggers'

export default {
	name: 'game',
	data() {
		return {
			game_width: 1920 * 3,
			board_left: 0,
			bg_width: 0,
			shaking: false
		}
	},
	computed: {
		...mapGetters([ 'lang', 'app', 'game', 'level', 'room', 'hero_modifiers' ]),
		talismans() {
			let slots = []

			this.game.hero.talismans.forEach((talisman) => {
				const exists = slots.find(s => s.slug === talisman.slug)
				if (exists) {
					exists.count++
				} else {
					slots.push({ slug: talisman.slug, talisman, count: 1 })
				}
			})

			return slots
		},
		current_room() {
			if (this.room >= 3) return 2
			return this.room
		}
	},
	watch: {
		'app.shaking'() {
			this.shaking = true
			setTimeout(() => {
				this.shaking = false
			}, 150)
		},
		'game.hero.position'(newValue, oldValue) {
			if (newValue > oldValue) Sounds.play('step')
		},
		'game.hero.gold'(newValue, oldValue) {
			if (newValue > oldValue) {
				Sounds.play('gold')
				fire_trigger('receive-gold', { from_character: this.game.hero })
				this.$store.dispatch('add_game_stat', { collected_emeralds: newValue - oldValue })
			}
		}
	},
	methods: {
		start_turn() {
			Sounds.play('go')
			this.$store.dispatch('start_turn')
		},
		resize() {
			const dropzone_width = this.$el.querySelector('.board .dropzone').clientWidth
			this.game_width = dropzone_width * 21 + 40
			this.board_left = (1920 - (dropzone_width * 7)) / 2
			this.bg_width = this.game_width + this.board_left * 2
		}
	},
	mounted() {
		window.addEventListener('resize', this.resize)
		this.resize()
	},
	created() {
		if (!this.game.hero) {
			if (process.env.NODE_ENV === 'development') {
				this.$store.dispatch('init_game', { hero: 'fish' })
			} else {
				this.$router.push({ name: 'home' })
			}
		}
	},
	components: { Background, Hand, Board, Shop, Talisman, UiGold, UiTooltip }
}
</script>

<style lang="stylus" scoped>

.game
	height 100%
	background-color dark
	&:before
		content ''
		position absolute
		left 0
		right 0
		top 50%
		height 4px
		background-color dark
	&.shaking
		animation shake 0.15s infinite ease-in-out
	.game-content
		height 100%
		transition 0.5s easeInOutSine
		for i in 0..3
			&[data-room=\"{i}\"]
				transform 'translateX(-%s)' % (cell_width * 7 * i)
	.board
		position absolute
		left 0
		right 0
		bottom 370px

	.start-turn-button
		position absolute
		right 40px
		bottom 40px
		width 179px
		height 179px
		border-radius 50%
		font-size 6.4rem
		text-align center
		color dark
		text-transform uppercase
		transition transform 0.1s easeOutQuart
		&:hover
			transform translateY(-4px)
			.button
				background-position bottom left
			.shadow
				transform translate(-6px, 8px)
		&:active
			transform translateY(4px)
			.shadow
				transform translate(-2px, 2px)
		.button, .shadow
			absolute 0
			background top left no-repeat
			background-size 179px (179px * 2)
		.shadow
			transform translate(-4px, 4px)
			transition transform 0.1s easeOutQuart
			filter brightness(0)
		.text
			absolute 0
			flex center center
	
	.hero-infos
		position absolute
		left 24px
		bottom 24px
		font-size 4rem
		color #fff
	
	.talismans
		position absolute
		left 50%
		top 8px
		padding 16px 24px
		flex flex-start flex-start
		background-color alpha(#000, 35%)
		border-radius 8px
		transform translateX(-50%)
		.talisman-wrap
			margin 0 24px 0 0 
			&:last-child
				margin 0
			&:hover
				>>> .ui-tooltip
					visibility visible
				>>> .talisman
					opacity 1
					transform scale(1.1)
			>>> .talisman
				width 40px
				height 40px
				transition transform 0.15s easeOutQuart
			.count
				position absolute
				right -8px
				bottom -8px
				width 24px
				height 24px
				background-color #fff
				border-radius 50%
				color dark
				text-align center
				flex center center

	.overlay
		absolute 0
		z-index 3
		&.opacity-leave-active
			transition 0.5s easeOutQuart
		&.opacity-leave-to
			opacity 0
		&.dark
			background-color dark




@keyframes shake
	0%, 100%
		transform translateX(0)
	10%
		transform translateX(-4px)
	20%
		transform translateX(2px)
	30%
		transform translateX(-4px)
	40%
		transform translateX(5px)
	50%
		transform translateX(-4px)
	60%
		transform translateX(3px)
	70%
		transform translateX(-5px)
	80%
		transform translateX(3px)
	90%
		transform translateX(-4px)


</style>